import {
  Content,
  ContentCriterionType,
  ContentValidationCriterion,
  ProductError,
  ProductErrorType,
} from '../../models';
import { CardDirection, CardIconType, CardSize } from '../card';

export class CardModalData {
  cardRows: CardModal[][];
  validationCriteria: ContentValidationCriterion[];
  cardDirection: CardDirection;
  cardSize: CardSize;
  title: string;

  constructor(cardData?: CardModalData) {
    this.cardRows = (cardData?.cardRows || []).map(row => (row || []).map(c => new CardModal(c)));
    this.cardDirection = cardData?.cardDirection || CardDirection.REGULAR;
    this.cardSize = cardData?.cardSize || CardSize.M;
    this.title = cardData?.title ?? null;
    this.validationCriteria = cardData
      ? (cardData?.validationCriteria || []).map(vc => new ContentValidationCriterion(vc))
      : [new ContentValidationCriterion({ type: ContentCriterionType.CLICK_ALL })]; // as default value only for initial creation
  }

  static validate(cardData: CardModalData, payloadData?: any): ProductError[] {
    if (!cardData || !cardData.cardRows || !cardData.cardRows.length) {
      return [{ type: ProductErrorType.MISSING, property: 'cardRows', payload: { ...(payloadData || {}) } }];
    }
    const errors = [];
    (cardData.cardRows || []).forEach((cardRow, cardRowIndex) => {
      if (Array.isArray(cardRow)) {
        (cardRow || []).forEach((card, cardIndex) => {
          CardModal.validate(
            card,
            { ...(payloadData ? payloadData : {}), cardRowIndex, cardIndex },
            cardData.cardDirection,
          ).forEach(err => errors.push(err));
        });
      } else {
        errors.push({
          type: ProductErrorType.BAD_FORMAT,
          property: 'cardRows',
          payload: { ...(payloadData ? payloadData : {}), cardRowIndex },
        });
      }
    });
    return errors;
  }
}

export class CardModal {
  slug: string;
  icon: string;
  iconType: CardIconType;
  title: string;
  contents: Content[];

  constructor(card?: CardModal) {
    this.slug = card ? card.slug : null;
    this.icon = card ? card.icon : null;
    this.iconType = card ? card.iconType : null;
    this.title = card ? card.title : null;
    this.contents = card ? (card.contents || []).map(c => new Content(c)) : [];
  }

  static validate(card: CardModal, payloadData: any, cardDirection: CardDirection): ProductError[][] {
    let errors = [];
    switch (cardDirection) {
      case CardDirection.REGULAR:
      case CardDirection.REVERSE:
        errors = ProductError.validatePrimitiveTypes(card, ['slug', 'title', 'icon', 'contents'], []).map(error => ({
          ...error,
          payload: { ...payloadData },
        }));
        break;
      case CardDirection.ONLY_ICON:
        errors = ProductError.validatePrimitiveTypes(card, ['slug', 'icon', 'contents']).map(error => ({
          ...error,
          payload: { ...payloadData },
        }));
        break;
      case CardDirection.ONLY_TEXT:
        errors = ProductError.validatePrimitiveTypes(card, ['slug', 'title', 'contents'], []).map(error => ({
          ...error,
          payload: { ...payloadData },
        }));
        break;
    }
    if (card.contents.length > 0) {
      card.contents.forEach((content, contentIndex) => {
        Content.validate(content, contentIndex, { ...payloadData, contentIndex }).forEach(err => errors.push(err));
      });
    } else {
      errors.push({ property: 'contents', type: ProductErrorType.MISSING, payload: { ...payloadData } });
    }
    return errors;
  }
}

export class CardModalContent {
  card: CardModal;
  validationCriteria: ContentValidationCriterion[];
  openTimeInSeconds: number;

  constructor(cardContent?: CardModalContent) {
    this.card = cardContent ? new CardModal(cardContent.card) : null;
    this.validationCriteria = cardContent
      ? (cardContent.validationCriteria || []).map(c => new ContentValidationCriterion(c))
      : [];
    this.openTimeInSeconds = cardContent ? cardContent.openTimeInSeconds : null;
  }
}

export class CardModalProgression {
  cardModalProgressionList?: CardModalProgressionItem[];
  constructor(progression?: CardModalProgression) {
    this.cardModalProgressionList = (progression?.cardModalProgressionList || []) //
      .map(v => new CardModalProgressionItem(v));
  }
}

export class CardModalProgressionItem {
  cardSlug: string;
  timerInSeconds: number;

  constructor(cardModalProgression?: CardModalProgressionItem) {
    this.cardSlug = cardModalProgression ? cardModalProgression.cardSlug : null;
    this.timerInSeconds = cardModalProgression ? cardModalProgression.timerInSeconds : null;
  }
}
